<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faPaperPlane,
  faHeadset,
  faComments,
  faHandshake,
  faLightbulb,
  faGlobeAmericas,
  faMobileAlt,
  faDesktop,
  faShieldAlt,
  faSyncAlt,
  faChartLine,
  faUsers,
  faCertificate,
  faAward,
  faRibbon,
  faStar,
  faThumbsUp,
  faHeart
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faPaperPlane,
  faHeadset,
  faComments,
  faHandshake,
  faLightbulb,
  faGlobeAmericas,
  faMobileAlt,
  faDesktop,
  faShieldAlt,
  faSyncAlt,
  faChartLine,
  faUsers,
  faCertificate,
  faAward,
  faRibbon,
  faStar,
  faThumbsUp,
  faHeart
)

// Animation on scroll
const animatedElements = ref<NodeListOf<Element>>()

onMounted(() => {
  animatedElements.value = document.querySelectorAll('.fade-in')

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in')
      }
    })
  }, { threshold: 0.1 })

  animatedElements.value.forEach(el => observer.observe(el))
})

import { contactService } from '@/api/contact'

// Form state
const formState = reactive({
  name: '',
  email: '',
  subject: '',
  message: '',
  agreement: false
})

const formErrors = reactive({
  name: '',
  email: '',
  subject: '',
  message: '',
  agreement: ''
})

const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref('')

// Validate form
const validateForm = () => {
  let isValid = true

  // Reset errors
  Object.keys(formErrors).forEach(key => {
    formErrors[key as keyof typeof formErrors] = ''
  })
  submitError.value = ''

  // Validate name
  if (!formState.name.trim()) {
    formErrors.name = 'Name is required'
    isValid = false
  }

  // Validate email
  if (!formState.email.trim()) {
    formErrors.email = 'Email is required'
    isValid = false
  } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
    formErrors.email = 'Please enter a valid email address'
    isValid = false
  }

  // Validate subject
  if (!formState.subject.trim()) {
    formErrors.subject = 'Subject is required'
    isValid = false
  }

  // Validate message
  if (!formState.message.trim()) {
    formErrors.message = 'Message is required'
    isValid = false
  } else if (formState.message.trim().length < 10) {
    formErrors.message = 'Message must be at least 10 characters'
    isValid = false
  }

  // Validate agreement
  if (!formState.agreement) {
    formErrors.agreement = 'You must agree to our privacy policy'
    isValid = false
  }

  return isValid
}

// Handle form submission
const handleSubmit = async (e: Event) => {
  e.preventDefault()

  if (validateForm()) {
    isSubmitting.value = true
    submitError.value = ''

    try {
      await contactService.sendMessage({
        name: formState.name,
        email: formState.email,
        subject: formState.subject,
        message: formState.message
      })
      
      submitSuccess.value = true
      
      // Reset form
      formState.name = ''
      formState.email = ''
      formState.subject = ''
      formState.message = ''
      formState.agreement = false
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        submitSuccess.value = false
      }, 5000)
    } catch (error) {
      console.error('Submission failed:', error)
      submitError.value = 'Failed to send message. Please try again later.'
    } finally {
      isSubmitting.value = false
    }
  }
}

// Contact methods
const contactMethods = ref([
  {
    icon: 'phone',
    title: 'Phone Support',
    description: 'Catherine Ngowi / Godfrey Shayo',
    details: '+255 756 588 876 / +255 622 088 754',
    link: 'tel:+255756588876',
    buttonText: 'Call Now'
  },
  {
    icon: 'envelope',
    title: 'Email Us',
    description: 'Send us an email and we\'ll respond quickly',
    details: 'swiryalm@yahoo.com',
    link: 'mailto:swiryalm@yahoo.com',
    buttonText: 'Send Email'
  },
  {
    icon: 'headset',
    title: 'Live Chat',
    description: 'Chat with our support team in real-time',
    details: '+255 754 972 917',
    link: 'https://wa.me/255754972917',
    buttonText: 'Start Chat'
  }
])

// Office locations
const offices = ref([
  {
    city: 'Headquarters',
    address: 'Tanzania, Dodoma',
    phone: '+255 754 972 917',
    email: 'swiryalm@yahoo.com',
    hours: 'Mon-Fri: 8am-6pm PST'
  },
  {
    city: 'Sales Office',
    address: 'Tanzania, Dodoma',
    phone: '+255 756 588 876',
    email: 'swiryalm@yahoo.com',
    hours: 'Mon-Fri: 9am-5pm GMT'
  },
  {
    city: 'Technical Office',
    address: 'Tanzania, Dodoma',
    phone: '+255 654 220 033, +255 775 405 763, +255 769 268 763',
    email: 'martinsanga230@gmail.com, salvationkamwaya77@gmail.com, sullegreygory@gmail.com',
    hours: 'Mon-Fri: 9am-6pm SGT'
  }
])

// FAQ items
const faqs = ref([
  {
    question: 'How quickly do you typically respond to support requests?',
    answer: 'We pride ourselves on our rapid response times. Most inquiries receive an initial response within 2 business hours, with emergency technical issues often addressed within 30 minutes during business hours.'
  },
  {
    question: 'Do you offer customized solutions for specific farming needs?',
    answer: 'Absolutely. Our platform is designed to be highly customizable. We work closely with farmers to understand their unique operations and can tailor features, reports, and integrations to meet specific requirements.'
  },
  {
    question: 'What training options are available for new users?',
    answer: 'We provide comprehensive onboarding including video tutorials, live webinars, documentation, and one-on-one training sessions. Our customer success team will ensure your team is confident using all platform features.'
  },
  {
    question: 'How does your pricing model work?',
    answer: 'We offer tiered pricing based on farm size and features needed. This includes a basic plan for small farms, professional plan for mid-sized operations, and enterprise solutions for large agricultural businesses. All plans include ongoing support and updates.'
  },
  {
    question: 'Can I integrate with existing farm equipment and software?',
    answer: 'Yes, our system is designed with interoperability in mind. We support integration with most major farm equipment brands, weather services, market data providers, and accounting software through our API and pre-built connectors.'
  },
  {
    question: 'What security measures protect my farm data?',
    answer: 'We implement enterprise-grade security including encryption in transit and at rest, regular security audits, two-factor authentication, and strict access controls. Your data belongs to you, and we never share it with third parties without your permission.'
  }
])

// Testimonials
const testimonials = ref([
  {
    name: 'Robert Johnson',
    farm: 'Sunny Valley Farms',
    location: 'Iowa, USA',
    content: 'This platform transformed how we manage our 2,000 acre operation. The yield forecasting alone has increased our profitability by 18% in the first year.',
    rating: 5
  },
  {
    name: 'Maria Rodriguez',
    farm: 'Organic Harvest Co-op',
    location: 'California, USA',
    content: 'The team management features have saved us countless hours in coordination. Our crew knows exactly what needs to be done each day, and we can track progress in real-time.',
    rating: 5
  },
  {
    name: 'James Wilson',
    farm: 'Wilson Family Farms',
    location: 'Ontario, Canada',
    content: 'As a fourth-generation farm, we were hesitant about new technology, but the intuitive design and excellent support made the transition smooth. We wish we had implemented this years earlier.',
    rating: 4
  },
  {
    name: 'Sarah Chen',
    farm: 'Green Growth Organics',
    location: 'Victoria, Australia',
    content: 'The weather integration and soil monitoring features have helped us reduce water usage by 30% while maintaining crop health. The environmental and cost benefits have been tremendous.',
    rating: 5
  }
])
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-green-50 to-white">
    <!-- Hero Section -->
    <section class="relative py-20 overflow-hidden bg-green-800">
      <div class="absolute inset-0 bg-black opacity-50"></div>
      <div class="relative container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center fade-in">
          <h1 class="text-4xl md:text-6xl font-bold text-white mb-6">Get In Touch</h1>
          <p class="text-xl text-green-100 mb-8">We're here to help you transform your farming operations. Reach out with questions, feedback, or to schedule a demo.</p>
        </div>
      </div>
    </section>

    <!-- Contact Methods -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12 fade-in">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">How Would You Like to Connect?</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">Choose the method that works best for you. Our team is ready to assist with any questions about our farm management platform.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div v-for="(method, index) in contactMethods" :key="index"
               class="bg-green-50 rounded-xl p-6 text-center transform transition duration-300 hover:scale-105 fade-in">
            <div class="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon :icon="method.icon" class="text-white text-2xl" />
            </div>
            <h3 class="text-xl font-semibold text-gray-800 mb-2">{{ method.title }}</h3>
            <p class="text-gray-600 mb-4">{{ method.description }}</p>
            <p class="text-green-700 font-medium mb-6">{{ method.details }}</p>
            <a :href="method.link" class="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition duration-300 inline-block">
              {{ method.buttonText }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Form -->
    <section class="py-16 bg-green-50">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div class="md:flex">
            <div class="md:w-1/2 bg-green-700 text-white p-8 md:p-12">
              <h2 class="text-2xl md:text-3xl font-bold mb-6">Send Us a Message</h2>
              <p class="mb-6">Have questions about our farm management platform? Fill out the form and our team will get back to you within 24 hours.</p>

              <div class="space-y-4">
                <div class="flex items-start">
                  <FontAwesomeIcon icon="clock" class="text-green-300 mt-1 mr-3" />
                  <div>
                    <h3 class="font-semibold">Response Time</h3>
                    <p class="text-green-100">Typically within 2 business hours</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <FontAwesomeIcon icon="headset" class="text-green-300 mt-1 mr-3" />
                  <div>
                    <h3 class="font-semibold">Support Hours</h3>
                    <p class="text-green-100">24/7 for critical issues</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <FontAwesomeIcon icon="users" class="text-green-300 mt-1 mr-3" />
                  <div>
                    <h3 class="font-semibold">Expert Assistance</h3>
                    <p class="text-green-100">Farm specialists and tech experts</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="md:w-1/2 p-8 md:p-12">
              <div v-if="submitSuccess" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                <p>Thank you for your message! We'll get back to you shortly.</p>
              </div>

              <div v-if="submitError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <p>{{ submitError }}</p>
              </div>

              <form @submit="handleSubmit" class="space-y-6">
                <div>
                  <label for="name" class="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    v-model="formState.name"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-300"
                    :class="formErrors.name ? 'border-red-500' : 'border-gray-300'"
                    placeholder="Your name">
                  <p v-if="formErrors.name" class="text-red-500 text-sm mt-1">{{ formErrors.name }}</p>
                </div>

                <div>
                  <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    v-model="formState.email"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-300"
                    :class="formErrors.email ? 'border-red-500' : 'border-gray-300'"
                    placeholder="your.email@example.com">
                  <p v-if="formErrors.email" class="text-red-500 text-sm mt-1">{{ formErrors.email }}</p>
                </div>

                <div>
                  <label for="subject" class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    v-model="formState.subject"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-300"
                    :class="formErrors.subject ? 'border-red-500' : 'border-gray-300'"
                    placeholder="What is this regarding?">
                  <p v-if="formErrors.subject" class="text-red-500 text-sm mt-1">{{ formErrors.subject }}</p>
                </div>

                <div>
                  <label for="message" class="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    id="message"
                    v-model="formState.message"
                    rows="4"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-300"
                    :class="formErrors.message ? 'border-red-500' : 'border-gray-300'"
                    placeholder="Tell us how we can help you..."></textarea>
                  <p v-if="formErrors.message" class="text-red-500 text-sm mt-1">{{ formErrors.message }}</p>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="agreement"
                      type="checkbox"
                      v-model="formState.agreement"
                      class="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded">
                  </div>
                  <div class="ml-3 text-sm">
                    <label for="agreement" class="font-medium text-gray-700">I agree to the privacy policy and terms of service</label>
                    <p v-if="formErrors.agreement" class="text-red-500 text-sm mt-1">{{ formErrors.agreement }}</p>
                  </div>
                </div>

                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center"
                  :class="isSubmitting ? 'opacity-75 cursor-not-allowed' : ''">
                  <span v-if="!isSubmitting">Send Message</span>
                  <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Offices Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12 fade-in">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Offices</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">We have teams around the world ready to support your agricultural needs.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div v-for="(office, index) in offices" :key="index"
               class="bg-green-50 rounded-xl p-6 transform transition duration-300 hover:scale-105 fade-in">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">{{ office.city }}</h3>

            <div class="space-y-4">
              <div class="flex items-start">
                <FontAwesomeIcon icon="map-marker-alt" class="text-green-600 mt-1 mr-3" />
                <p class="text-gray-600">{{ office.address }}</p>
              </div>

              <div class="flex items-start">
                <FontAwesomeIcon icon="phone" class="text-green-600 mt-1 mr-3" />
                <p class="text-gray-600">{{ office.phone }}</p>
              </div>

              <div class="flex items-start">
                <FontAwesomeIcon icon="envelope" class="text-green-600 mt-1 mr-3" />
                <p class="text-gray-600">{{ office.email }}</p>
              </div>

              <div class="flex items-start">
                <FontAwesomeIcon icon="clock" class="text-green-600 mt-1 mr-3" />
                <p class="text-gray-600">{{ office.hours }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-green-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12 fade-in">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">Find answers to common questions about our farm management platform.</p>
        </div>

        <div class="max-w-4xl mx-auto space-y-6">
          <div v-for="(faq, index) in faqs" :key="index"
               class="bg-white rounded-xl p-6 shadow-sm transition duration-300 hover:shadow-md fade-in">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">{{ faq.question }}</h3>
            <p class="text-gray-600">{{ faq.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12 fade-in">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">What Farmers Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">Hear from agricultural professionals who are using our platform to transform their operations.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div v-for="(testimonial, index) in testimonials" :key="index"
               class="bg-green-50 rounded-xl p-6 transform transition duration-300 hover:scale-105 fade-in">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                {{ testimonial.name.charAt(0) }}
              </div>
              <div>
                <h3 class="font-semibold text-gray-800">{{ testimonial.name }}</h3>
                <p class="text-sm text-gray-600">{{ testimonial.farm }}, {{ testimonial.location }}</p>
              </div>
            </div>

            <div class="flex mb-4">
              <FontAwesomeIcon v-for="star in 5" :key="star"
                               :icon="star <= testimonial.rating ? 'star' : ['far', 'star']"
                               class="text-yellow-400" />
            </div>

            <p class="text-gray-600 italic">"{{ testimonial.content }}"</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-green-800 text-white">
      <div class="container mx-auto px-4 text-center fade-in">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
        <p class="text-xl mb-8 max-w-3xl mx-auto">Join the thousands of farmers who have transformed their operations with our platform.</p>
        <div class="flex flex-col sm:flex-row justify-center gap-4">
          <a href="#message" class="bg-white text-green-800 font-bold py-3 px-8 rounded-lg transition duration-300 hover:bg-green-100 inline-block">
            Request a Demo
          </a>
          <a href="/login" class="border-2 border-white text-white font-bold py-3 px-8 rounded-lg transition duration-300 hover:bg-white hover:text-green-800 inline-block">
            Login
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.animate-fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* Custom animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.8s forwards;
}

/* Delay animations for staggered effect */
.fade-in:nth-child(1) { animation-delay: 0.1s; }
.fade-in:nth-child(2) { animation-delay: 0.2s; }
.fade-in:nth-child(3) { animation-delay: 0.3s; }
.fade-in:nth-child(4) { animation-delay: 0.4s; }
.fade-in:nth-child(5) { animation-delay: 0.5s; }
.fade-in:nth-child(6) { animation-delay: 0.6s; }
</style>
